import React from "react";
import { Tabs, Tab, Tooltip, OverlayTrigger, Row, Col } from "react-bootstrap";
import DemographicsBarChart from "../../../components/DemographicBarChart";
import DemoTable from "./DemoTable";
import {
  COLOR_POSITIVE,
  COLOR_DEATHS,
  COLOR_TESTED,
} from "../../../GlobalColors";

interface DemographicsProps {
  data: App.Demo;
  currentFilter: App.CurrentFilter;
}

const Demographics = (props: DemographicsProps) => {
  const { data, currentFilter } = props;

  return (
    <>
      <div className="m-demographics-header d-flex justify-content-between align-items-center">
        <h3 className="mb-3">Demographic Distributions</h3>
        <OverlayTrigger
          key="bottom"
          placement="bottom"
          overlay={
            <Tooltip id="tooltip-bottom">
              Many cases reported to IDOH have race and ethnicity marked as
              unknown. Counts reported as unknown or missing in the IDOH case
              reporting system (NBS) have been updated when possible using
              Indiana Public Health Surveillance System.
            </Tooltip>
          }
        >
          <p
            className="mt-1 text-right cursor-pointer"
            style={{ textDecoration: "underline dotted" }}
          >
            {" "}
            Why do I see "Unknowns"?{" "}
          </p>
        </OverlayTrigger>
      </div>
      <Tabs defaultActiveKey="demographics-cases" id="demographics-charts">
        <Tab eventKey="demographics-cases" title="Positive Cases">
          <Row>
            <Col>
              <h5>
                {currentFilter.filters.length > 0
                  ? currentFilter.displayName
                  : "Statewide"}{" "}
                Demographics for Positive Cases*
              </h5>
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={6} className="m-demographics-left">
              <DemographicsBarChart
                color={COLOR_POSITIVE}
                data={data.ageCases}
                suppress={false}
                demoType="age"
                height={385}
                ltc={false}
              />
              <DemographicsBarChart
                demoType={"Gender"}
                height={200}
                data={data.genderCases}
                suppress={false}
                color={COLOR_POSITIVE}
                ltc={false}
              />
            </Col>
            <Col xs={12} lg={6} className="m-demographics-right">
              <DemoTable
                data={data.raceCases}
                demoType={"Race"}
                eventType={"Cases"}
                suppress={false}
                showPop={!currentFilter.filters.length}
              />
              <DemoTable
                data={data.ethnicityCases}
                demoType={"Ethnicity"}
                eventType={"Cases"}
                suppress={false}
                showPop={!currentFilter.filters.length}
              />
            </Col>
          </Row>
        </Tab>
        <Tab eventKey="demographics-deaths" title="Deaths">
          <Row>
            <Col>
              <h5>
                {currentFilter.filters.length
                  ? currentFilter.displayName
                  : "Statewide"}{" "}
                Demographics for Deaths*
              </h5>
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={6} className="m-demographics-left">
              <DemographicsBarChart
                color={COLOR_DEATHS}
                data={data.ageDeaths}
                suppress={data.suppressDeath}
                height={385}
                demoType="age"
                ltc={false}
              />
              <DemographicsBarChart
                demoType={"Gender"}
                height={200}
                data={data.genderDeaths}
                suppress={data.suppressDeath}
                color={COLOR_DEATHS}
                ltc={false}
              />
            </Col>
            <Col xs={12} lg={6} className="m-demographics-right">
              <DemoTable
                data={data.raceDeaths}
                demoType={"Race"}
                eventType={"Deaths"}
                suppress={data.suppressDeath}
                showPop={!currentFilter.filters.length}
              />
              <DemoTable
                data={data.ethnicityDeaths}
                demoType={"Ethnicity"}
                eventType={"Deaths"}
                suppress={data.suppressDeath}
                showPop={!currentFilter.filters.length}
              />
            </Col>
          </Row>
        </Tab>
        <Tab eventKey="demographics-tested" title="Tested Individuals">
          <Row>
            <Col>
              <h5>
                {currentFilter.filters.length > 0
                  ? currentFilter.displayName
                  : "Statewide"}{" "}
                Demographics for Tests*
              </h5>
            </Col>
          </Row>
          <Row>
            <Col xs={12} lg={6} className="m-demographics-left">
              <DemographicsBarChart
                color={COLOR_TESTED}
                data={data.ageTests}
                suppress={false}
                height={385}
                demoType="age"
                ltc={false}
              />
              <DemographicsBarChart
                demoType={"Gender"}
                height={200}
                data={data.genderTests}
                suppress={false}
                color={COLOR_TESTED}
                ltc={false}
              />
            </Col>
            <Col xs={12} lg={6} className="m-demographics-right">
              <DemoTable
                data={data.raceTests}
                demoType={"Race"}
                eventType={"Tests"}
                suppress={false}
                showPop={!currentFilter.filters.length}
              />
              <DemoTable
                data={data.ethnicityTests}
                demoType={"Ethnicity"}
                eventType={"Tests"}
                suppress={false}
                showPop={!currentFilter.filters.length}
              />
            </Col>
          </Row>
        </Tab>
      </Tabs>
      <hr />
      <p className="font-italic card-footnote">
        * Indiana population percentages provided by 2019 U.S. Census Bureau;
        Population Estimate Program.
      </p>
    </>
  );
};

export default Demographics;
